import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, UpdateAPIRequest } from '../types/api';
import { apiService } from '../services/apiService';
import './APIDetails.css';

interface APIDetailsState {
  api: API | null;
  loading: boolean;
  error: string | null;
  isEditing: boolean;
  showDeleteConfirm: boolean;
  editForm: UpdateAPIRequest;
  saving: boolean;
  deleting: boolean;
}

export const APIDetailsFixed: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [state, setState] = useState<APIDetailsState>({
    api: null,
    loading: true,
    error: null,
    isEditing: false,
    showDeleteConfirm: false,
    editForm: {},
    saving: false,
    deleting: false,
  });

  const loadAPI = useCallback(async () => {
    if (!id) {
      setState(prev => ({ ...prev, error: 'API ID is required', loading: false }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      console.log('Loading API with ID:', id);
      const api = await apiService.getAPI(id);
      console.log('API loaded:', api);
      
      setState(prev => ({ 
        ...prev, 
        api, 
        loading: false,
        editForm: {
          name: api.name,
          description: api.description,
          url: api.url,
          status: api.status === 'deleted' ? 'deprecated' : api.status as 'active' | 'inactive' | 'deprecated',
          tags: api.tags,
          version: api.version,
          documentation: api.documentation,
          authentication: api.authentication,
          rateLimit: api.rateLimit,
          payloadDescription: api.payloadDescription,
        }
      }));
    } catch (error: any) {
      console.error('Error loading API:', error);
      const errorMessage = error.status === 404 ? 'API not found' : error.message || 'Failed to load API';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
    }
  }, [id]);

  useEffect(() => {
    loadAPI();
  }, [loadAPI]);

  const handleEdit = () => {
    setState(prev => ({ ...prev, isEditing: true }));
  };

  const handleCancelEdit = () => {
    if (state.api) {
      setState(prev => ({ 
        ...prev, 
        isEditing: false,
        editForm: {
          name: state.api!.name,
          description: state.api!.description,
          url: state.api!.url,
          status: state.api!.status === 'deleted' ? 'deprecated' : state.api!.status as 'active' | 'inactive' | 'deprecated',
          tags: state.api!.tags,
          version: state.api!.version,
          documentation: state.api!.documentation,
          authentication: state.api!.authentication,
          rateLimit: state.api!.rateLimit,
        }
      }));
    }
  };

  const handleSaveEdit = async () => {
    if (!id || !state.editForm.name?.trim()) {
      setState(prev => ({ ...prev, error: 'Name is required' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, saving: true, error: null }));
      const updatedAPI = await apiService.updateAPI(id, state.editForm);
      setState(prev => ({ 
        ...prev, 
        api: updatedAPI, 
        isEditing: false, 
        saving: false 
      }));
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to update API: ' + (error.message || 'Unknown error'), 
        saving: false 
      }));
    }
  };

  const handleDelete = () => {
    setState(prev => ({ ...prev, showDeleteConfirm: true }));
  };

  const handleCancelDelete = () => {
    setState(prev => ({ ...prev, showDeleteConfirm: false }));
  };

  const handleConfirmDelete = async () => {
    if (!id) return;

    try {
      setState(prev => ({ ...prev, deleting: true, error: null }));
      await apiService.deleteAPI(id);
      navigate('/');
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to delete API: ' + (error.message || 'Unknown error'), 
        deleting: false,
        showDeleteConfirm: false
      }));
    }
  };

  const handleInputChange = (field: keyof UpdateAPIRequest, value: any) => {
    setState(prev => ({
      ...prev,
      editForm: { ...prev.editForm, [field]: value }
    }));
  };

  const handleTagsChange = (tagsString: string) => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    handleInputChange('tags', tags);
  };

  const formatDate = (date: Date) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(date));
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusClass = (status: string) => {
    return `status-badge status-${status}`;
  };

  if (state.loading) {
    return (
      <div className="api-details">
        <div className="loading">Loading API details...</div>
      </div>
    );
  }

  if (state.error && !state.api) {
    return (
      <div className="api-details">
        <div className="error">
          <h2>Error loading API details</h2>
          <p>{state.error}</p>
          <button onClick={loadAPI} className="btn btn-primary">
            Try Again
          </button>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!state.api) {
    return (
      <div className="api-details">
        <div className="error">
          <h2>API not found</h2>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="api-details">
      <div className="api-details-header">
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          Back to Dashboard
        </button>
        <div className="api-details-actions">
          {!state.isEditing && (
            <>
              <button onClick={handleEdit} className="btn btn-primary">
                Edit API
              </button>
              <button onClick={handleDelete} className="btn btn-danger">
                Delete API
              </button>
            </>
          )}
        </div>
      </div>

      {state.error && (
        <div className="error-message">
          {state.error}
        </div>
      )}

      {state.isEditing ? (
        <div className="api-edit-form">
          <h1>Edit API</h1>
          <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                id="name"
                type="text"
                value={state.editForm.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                value={state.editForm.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="payloadDescription">
                Payload Description
                <span className="field-hint">Describe the expected request payload format for AI test data generation</span>
              </label>
              <textarea
                id="payloadDescription"
                value={state.editForm.payloadDescription || ''}
                onChange={(e) => handleInputChange('payloadDescription', e.target.value)}
                placeholder="e.g., User registration data with name, email, password, and optional phone number"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">URL *</label>
              <input
                id="url"
                type="url"
                value={state.editForm.url || ''}
                onChange={(e) => handleInputChange('url', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={state.editForm.status || 'active'}
                onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'inactive' | 'deprecated')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                id="tags"
                type="text"
                value={state.editForm.tags?.join(', ') || ''}
                onChange={(e) => handleTagsChange(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="version">Version</label>
              <input
                id="version"
                type="text"
                value={state.editForm.version || ''}
                onChange={(e) => handleInputChange('version', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="documentation">Documentation URL</label>
              <input
                id="documentation"
                type="url"
                value={state.editForm.documentation || ''}
                onChange={(e) => handleInputChange('documentation', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="authentication">Authentication</label>
              <input
                id="authentication"
                type="text"
                value={state.editForm.authentication || ''}
                onChange={(e) => handleInputChange('authentication', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rateLimit">Rate Limit</label>
              <input
                id="rateLimit"
                type="text"
                value={state.editForm.rateLimit || ''}
                onChange={(e) => handleInputChange('rateLimit', e.target.value)}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={state.saving}>
                {state.saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="api-details-content">
          <div className="api-header">
            <h1>{state.api.name}</h1>
            <span className={getStatusClass(state.api.status)}>
              {state.api.status.charAt(0).toUpperCase() + state.api.status.slice(1)}
            </span>
          </div>

          <div className="api-info">
            <div className="info-section">
              <h2>Description</h2>
              <p>{state.api.description}</p>
            </div>

            <div className="info-section">
              <h2>API Details</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>URL:</label>
                  <span>{state.api.url}</span>
                </div>
                <div className="info-item">
                  <label>Version:</label>
                  <span>{state.api.version || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <label>Authentication:</label>
                  <span>{state.api.authentication || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <label>Rate Limit:</label>
                  <span>{state.api.rateLimit || 'Not specified'}</span>
                </div>
              </div>
            </div>

            {state.api.documentation && (
              <div className="info-section">
                <h2>Documentation</h2>
                <a 
                  href={state.api.documentation} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="doc-link"
                >
                  View Documentation
                </a>
              </div>
            )}

            <div className="info-section">
              <h2>Tags</h2>
              <div className="tags">
                {state.api.tags?.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                )) || <span>No tags</span>}
              </div>
            </div>

            <div className="info-section">
              <h2>Metadata</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Created:</label>
                  <span>{formatDate(state.api.createdAt)} by {state.api.createdBy}</span>
                </div>
                <div className="info-item">
                  <label>Updated:</label>
                  <span>{formatDate(state.api.updatedAt)} by {state.api.updatedBy}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {state.showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete "{state.api.name}"?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                onClick={handleConfirmDelete} 
                className="btn btn-danger"
                disabled={state.deleting}
              >
                {state.deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button onClick={handleCancelDelete} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Testing Panel */}
      {state.api && !state.loading && (
        <div style={{backgroundColor: '#f0f8ff', border: '2px solid #007cba', padding: '20px', margin: '20px 0', borderRadius: '8px'}}>
          <h2>🧪 API Testing</h2>
          <p><strong>API:</strong> {state.api.name}</p>
          <p><strong>URL:</strong> {state.api.url}</p>
          {state.api.payloadDescription && (
            <div style={{marginBottom: '15px'}}>
              <h3>Payload Description:</h3>
              <pre style={{backgroundColor: '#f5f5f5', padding: '10px', whiteSpace: 'pre-wrap', borderRadius: '4px'}}>
                {state.api.payloadDescription}
              </pre>
            </div>
          )}
          <div style={{marginTop: '15px'}}>
            <button 
              style={{
                padding: '10px 20px', 
                backgroundColor: '#007cba', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
              onClick={async () => {
                try {
                  const response = await fetch('/api/v1/enhanced-proxy/generate-test-data', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      apiId: state.api?.id,
                      method: 'POST',
                      style: 'realistic',
                      customContext: 'Generate test data for this API'
                    })
                  });
                  const result = await response.json();
                  alert('Generated test data: ' + JSON.stringify(result, null, 2));
                } catch (error) {
                  alert('Error: ' + error);
                }
              }}
            >
              🎲 Generate Test Data
            </button>
            <button 
              style={{
                padding: '10px 20px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={async () => {
                try {
                  const response = await fetch('/api/v1/enhanced-proxy/generate-and-test', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      apiId: state.api?.id,
                      method: 'POST',
                      style: 'realistic',
                      autoExecute: true
                    })
                  });
                  const result = await response.json();
                  alert('API Test Result: ' + JSON.stringify(result, null, 2));
                } catch (error) {
                  alert('Error: ' + error);
                }
              }}
            >
              🚀 Test API
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
